import type { RefObject } from 'react';
import React, { useEffect, useState } from 'react';
import { Drawer } from '@noths/polaris-client-ribbons-design-system';
import type { Review } from '@noths/polaris-design-system-ooux-interfaces';

import * as copy from '../constants/copy';
import type { CustomersReviewsDrawerContentProps } from './CustomersReviewsDrawerContent';
import { CustomersReviewsDrawerContent } from './CustomersReviewsDrawerContent';
import { ReviewDrawer } from './ReviewDrawer';
import * as styles from './CustomersReviewsDrawer.styles';

export interface CustomersReviewsDrawerProps extends CustomersReviewsDrawerContentProps {
  isOpen: boolean;
  /** The review a `#reviews-drawer-{id}` link asked for; null when it is not on the loaded page. */
  linkedReview: Review | null;
  onBack: () => void;
  /** The buy-box column the panel aligns its left edge to. */
  target: RefObject<HTMLElement | null>;
}

/**
 * Every review for the product, paged, in a panel: the whole of a phone, and from 768px up
 * a side panel aligned to the buy-box column like the delivery drawer. Open state comes
 * from the URL, so closing is a history step back and the browser's back button closes it too.
 */
export const CustomersReviewsDrawer = ({
  isOpen,
  linkedReview,
  onBack,
  target,
  ...content
}: CustomersReviewsDrawerProps) => {
  const [width, setWidth] = useState<number>();

  // From the buy-box column's left edge to the viewport's right edge, so the panel lines up
  // with the delivery drawer. Below 768px the stylesheet clamps it to the whole screen.
  useEffect(() => {
    const measure = () => {
      const left = target.current?.getBoundingClientRect().left;

      setWidth(left === undefined ? undefined : Math.round(window.innerWidth - left));
    };

    measure();
    window.addEventListener('resize', measure);

    return () => window.removeEventListener('resize', measure);
  }, [target]);

  return (
    <>
      <Drawer
        contentLabel={copy.DRAWER_CONTENT_LABEL}
        isOpen={isOpen}
        onRequestClose={content.onClose}
      >
        <div
          css={styles.panel}
          data-testid="reviews-drawer-panel"
          style={width === undefined ? undefined : { width }}
        >
          <CustomersReviewsDrawerContent {...content} />
        </div>
      </Drawer>
      {/* A sibling of the list's panel, not a child: both portal to the body and the later
          one sits on top, rather than nesting inside the list's scroll container. */}
      {linkedReview && (
        <ReviewDrawer
          date={content.date}
          isOpen={isOpen}
          onBack={onBack}
          review={linkedReview}
          width={width}
        />
      )}
    </>
  );
};

CustomersReviewsDrawer.displayName = 'CustomersReviewsDrawer';
